import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import PartyName from "./PartyName";
import NotesField from "./NotesField";
import CarrierChecklist from "./CarrierChecklist";
import { requestLines } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";
import { isInFuture, shareFields, toIsoUtc, toLocalInput, validateShares } from "./placingUtils";

const asInput = (n) => (n == null ? "" : String(n));

/**
 * The nominated leads, from the quote comparison: each nominated quote request
 * (or nominated quote) with the quote being turned into a lead line.
 */
export const nominatedLeads = (comparison) =>
  (comparison?.rows || [])
    .map((row) => {
      const quotes = row.quotes || [];
      const quote = quotes.find((q) => q.status === "nominated") || (row.status === "nominated" ? quotes[0] : null);
      return quote || row.status === "nominated" ? { partyId: row.partyId, quote } : null;
    })
    .filter(Boolean)
    // one per carrier (a carrier has at most one nominated quote)
    .filter((lead, i, all) => all.findIndex((l) => l.partyId === lead.partyId) === i);

const sharesFromQuote = (quote) => ({
  requestedShare: asInput(quote?.quotedShare ?? quote?.maximumShare),
  minimumShare: asInput(quote?.minimumShare),
  maximumShare: asInput(quote?.maximumShare),
});

/**
 * Broker: request lines (Order). role "lead" sends to the nominated leads
 * (shares from each lead's nominated quote); role "follower" to any other
 * carriers. One line request per party.
 */
const LineRequestForm = ({ role, placementId, submission, comparison, lines, onSent }) => {
  const toast = useToast();
  const isLead = role === "lead";
  const leads = useMemo(() => nominatedLeads(comparison), [comparison]);
  const leadIds = leads.map((l) => l.partyId);
  const [partyIds, setPartyIds] = useState(isLead ? leadIds : []);
  const [form, setForm] = useState({
    requestedShare: "",
    minimumShare: "",
    maximumShare: "",
    participationBasis: "",
    currency: isLead ? "" : "GBP",
    responseRequiredByDate: toLocalInput(submission?.responseRequiredByDate),
    notes: "",
  });
  const [sending, setSending] = useState(false);

  // Leads that already have an open lead line request (sent or offered)
  const alreadyRequested = (lines?.rows || [])
    .filter((r) => r.role === "lead" && ["sent", "offered"].includes(r.status))
    .map((r) => r.partyId);

  // Pre-tick the nominated leads not yet asked for a lead line (again if the
  // comparison or lines arrive later); the others can still be ticked
  const preTickKey = leadIds.filter((id) => !alreadyRequested.includes(id)).join(",");
  useEffect(() => {
    if (isLead) setPartyIds(preTickKey ? preTickKey.split(",") : []);
  }, [isLead, preTickKey]);

  // One lead: start from its nominated quote's shares. Several: leave them
  // blank so the API takes each lead's from its own quote.
  const leadKey = isLead ? partyIds.join(",") : "";
  useEffect(() => {
    if (!isLead) return;
    const chosen = leads.filter((l) => partyIds.includes(l.partyId));
    setForm((f) => ({ ...f, ...(chosen.length === 1 ? sharesFromQuote(chosen[0].quote) : { requestedShare: "", minimumShare: "", maximumShare: "" }) }));
    // leadKey stands for partyIds
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadKey, leads]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const toggleLead = (id) => setPartyIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  const sharesGiven = form.requestedShare !== "" || form.minimumShare !== "" || form.maximumShare !== "";

  const send = async () => {
    if (partyIds.length === 0) return toast({ title: isLead ? "Choose a lead" : "Choose at least one follower", status: "warning" });
    // Leads may leave the shares to the API (from the quote); followers give a requested share
    if (!isLead || sharesGiven) {
      const shareError = validateShares({ share: form.requestedShare, minimumShare: form.minimumShare, maximumShare: form.maximumShare });
      if (shareError) return toast({ title: shareError, status: "warning" });
    }
    if (form.responseRequiredByDate && !isInFuture(form.responseRequiredByDate)) {
      return toast({ title: "Response date must be in the future", status: "warning" });
    }

    setSending(true);
    try {
      const created = await requestLines(placementId, {
        role,
        partyIds,
        ...shareFields({ requestedShare: form.requestedShare, minimumShare: form.minimumShare, maximumShare: form.maximumShare }),
        ...(form.participationBasis && { participationBasis: form.participationBasis }),
        ...(form.currency && { currency: form.currency }),
        ...(form.responseRequiredByDate && { responseRequiredByDate: toIsoUtc(form.responseRequiredByDate) }),
        ...(form.notes.trim() && { notes: form.notes.trim() }),
      });
      const count = Array.isArray(created) ? created.length : partyIds.length;
      toast({ title: `${count} ${isLead ? "lead" : "follower"} line request(s) sent`, status: "success" });
      if (!isLead) setPartyIds([]);
      await onSent();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't request lines", duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontSize="xs" color="gray.500" mb={1}>{isLead ? "Nominated leads" : "Followers"}</Text>
        {isLead ? (
          leads.length === 0 ? (
            <Text fontSize="sm" color="gray.500">No lead has been nominated yet.</Text>
          ) : (
            <VStack align="stretch" spacing={1}>
              {leads.map((lead) => (
                <Checkbox key={lead.partyId} isChecked={partyIds.includes(lead.partyId)} onChange={() => toggleLead(lead.partyId)}>
                  <PartyName partyId={lead.partyId} />
                  {alreadyRequested.includes(lead.partyId) && (
                    <Text as="span" fontSize="xs" color="orange.600"> · lead line already requested</Text>
                  )}
                  {lead.quote && (
                    <Text as="span" fontSize="xs" color="gray.500">
                      {" "}· nominated quote {lead.quote.reference || "(no reference)"}
                      {lead.quote.quotedShare != null && ` at ${lead.quote.quotedShare}%`}
                    </Text>
                  )}
                </Checkbox>
              ))}
            </VStack>
          )
        ) : (
          <Box maxH="220px" overflowY="auto">
            {/* The broker's own party and the leads (who get a lead line request) can't follow */}
            <CarrierChecklist value={partyIds} onChange={setPartyIds} excludeIds={[submission?.sender?.partyId, ...leadIds]} />
          </Box>
        )}
      </Box>

      <SimpleGrid columns={[1, 3]} spacing={3}>
        <FormControl isRequired={!isLead}>
          <FormLabel fontSize="xs">Requested share %</FormLabel>
          <Input size="sm" type="number" min={0} max={100} step="any" value={form.requestedShare} onChange={set("requestedShare")} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Minimum share %</FormLabel>
          <Input size="sm" type="number" min={0} max={100} step="any" value={form.minimumShare} onChange={set("minimumShare")} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Maximum share %</FormLabel>
          <Input size="sm" type="number" min={0} max={100} step="any" value={form.maximumShare} onChange={set("maximumShare")} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Participation basis</FormLabel>
          <Input size="sm" value={form.participationBasis} onChange={set("participationBasis")} placeholder={isLead ? "From the quote" : ""} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Currency</FormLabel>
          <Input size="sm" value={form.currency} onChange={set("currency")} placeholder={isLead ? "From the quote" : ""} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Response required by</FormLabel>
          <Input size="sm" type="datetime-local" value={form.responseRequiredByDate} onChange={set("responseRequiredByDate")} />
        </FormControl>
      </SimpleGrid>
      {isLead && partyIds.length > 1 && (
        <Text fontSize="xs" color="gray.500">Left blank, each lead's shares come from its nominated quote.</Text>
      )}
      <NotesField
        label={isLead ? "Notes for the lead(s)" : "Notes for the followers"}
        value={form.notes}
        onChange={(notes) => setForm((f) => ({ ...f, notes }))}
      />
      <HStack>
        <Button size="sm" colorScheme="blue" onClick={send} isLoading={sending} isDisabled={partyIds.length === 0}>
          Request {isLead ? "lead" : "follower"} line{partyIds.length === 1 ? "" : "s"} ({partyIds.length})
        </Button>
      </HStack>
    </VStack>
  );
};

export default LineRequestForm;
